import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import type { Episode, MediaItem, NarrationStatus } from '../types';
import { formatDateRange, formatFullDate } from '../lib/format';
import type { PlayerPreferences, SoundtrackLibrary } from './types';
import { COLOR_FILTERS, episodeOverrides } from './episodeLook';
import { buildShots } from './shots';
import { INTRO_INDEX, INTRO_MS, useFilmEngine, type FrameInfo } from './useFilmEngine';
import { usePlayerPreferences } from './usePlayerPreferences';
import { DUCK_LEVEL, SoundtrackMixer, VOICE_DUCK_LEVEL } from './soundtrack';

import { ShotLayer } from './components/ShotLayer';
import { EndingCard, OpeningCard } from './components/TitleCard';
import { Filmstrip } from './components/Filmstrip';
import { SettingsPanel } from './components/SettingsPanel';
import { StoryText } from '../components/StoryPanel';
import {
  clipBusy,
  labelOf,
  narrationSnapshot,
  onNarrationChange,
  pauseClip,
  playClip,
  prefetchClip,
  resetNarration,
  resumeClip,
  stopClip,
} from './narrationAudio';
import { clipLengths, primaryLanguage, savedVoice } from './voices';
import { exportHost, recordForHost } from './filmRecorder';
import DownloadVideoButton from '../components/DownloadVideoButton';

/** How long the music stays down after a line ends (covers the gap to the next one). */
const VOICE_RELEASE_MS = 1500;

interface Props {
  episode: Episode;
  items: MediaItem[];
  seasonTitle: string | null;
  nextEpisode: { id: string; title: string } | null;
  onExit: () => void;
  onNext: (episodeId: string) => void;
  onSaveProgress: (episodeId: string, seconds: number, completed: boolean) => void;
  /** Narration clips (both languages) and their lengths; null when not known. */
  narration?: NarrationStatus | null;
  /** Play audio and subtitles in this language (from the story's Preview), until the viewer changes it. */
  langOverride?: 'en' | 'ur' | null;
  /** Export mode (opened by the server's hidden Chrome): record the film in this language. */
  exportLang?: 'en' | 'ur' | null;
  /** Only the owner can download the video. */
  canDownload?: boolean;
}

const CHROME_HIDE_MS = 3000;
/** Share of a video's own volume kept while a story line is spoken over it. */
const VIDEO_UNDER_VOICE = 0.3;
/** The opening is spoken once the title has risen. */
const OPENING_LEAD_MS = 600;

function useSystemReducedMotion() {
  const [reduced, setReduced] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduced(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return reduced;
}

function useViewportAspect() {
  const [aspect, setAspect] = useState(() => window.innerWidth / Math.max(1, window.innerHeight));
  useEffect(() => {
    const on = () => setAspect(window.innerWidth / Math.max(1, window.innerHeight));
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return aspect;
}

function clock(ms: number) {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

const TRUSTED = new Set(['EXIF_DT_ORIGINAL', 'EXIF_CREATE_DATE', 'EXIF_OFFSET_DT_ORIGINAL', 'VIDEO_CREATION_TIME']);

/**
 * The story film: opening title, every memory with motion and transitions,
 * an optional soundtrack, and a closing card.
 */
export default function FilmPlayer({
  episode,
  items,
  seasonTitle,
  nextEpisode,
  onExit,
  onNext,
  onSaveProgress,
  narration: narrationStatus = null,
  langOverride = null,
  exportLang = null,
  canDownload = true,
}: Props) {
  const { prefs: savedPrefs, update: savePrefs, loaded: prefsLoaded } = usePlayerPreferences();
  // This episode's own music and look win over the saved preferences until
  // the viewer changes that setting here.
  const [overrides, setOverrides] = useState(() => episodeOverrides(episode.setup));
  const prefs = useMemo(() => ({ ...savedPrefs, ...overrides }), [savedPrefs, overrides]);
  const update = useCallback(
    (patch: Partial<PlayerPreferences>) => {
      setOverrides((o) => {
        const next = { ...o };
        for (const k of Object.keys(patch) as Array<keyof PlayerPreferences>) delete next[k];
        return next;
      });
      savePrefs(patch);
    },
    [savePrefs],
  );
  const systemReduced = useSystemReducedMotion();
  const choice = prefs.photoAnimation;
  // An explicit choice to animate is consent to motion, even when the device
  // asks for less; until the viewer chooses, the device setting decides.
  const reducedMotion =
    prefs.reducedMotion === 'on' || (prefs.reducedMotion === 'system' && systemReduced && choice === 'ask');
  const photoMotion: 'still' | 'motion' | 'depth' =
    choice === 'ask' ? (systemReduced ? 'still' : 'depth') : choice;
  const hasDepth = useMemo(() => items.some((i) => i.type === 'PHOTO' && i.depthUrl), [items]);
  const viewportAspect = useViewportAspect();

  // The opening card choice is read once, so toggling it mid-film doesn't
  // restart anything; it applies next time.
  const [openingCard] = useState(prefs.openingCard || exportLang !== null);

  // ---- AI story lines ("Make it a story") ------------------------------------
  // Every story has English and Roman Urdu text and audio. The viewer picks the
  // audio and the subtitles separately (saved); "story" means the story's own
  // first language. A Preview from the story panel picks both for this visit.
  const story = episode.story ?? null;
  const primary = primaryLanguage(story);
  const [override, setOverride] = useState<'en' | 'ur' | null>(exportLang ?? langOverride);
  const audioLang: 'en' | 'ur' | null = !story
    ? null
    : (override ?? (prefs.narration === 'off' ? null : prefs.narration === 'story' ? primary : prefs.narration));
  const subtitleLang: 'en' | 'ur' | null = !story
    ? null
    : (override ??
      (prefs.storyLines === 'off' || prefs.subtitles === 'off' ? null : prefs.subtitles === 'story' ? primary : prefs.subtitles));
  const storyLines = useMemo(() => new Map((story?.lines ?? []).map((l) => [l.mediaId, l])), [story]);
  const voice = audioLang ? savedVoice(prefs, audioLang) : null;
  const lengths = useMemo(
    () => (story && audioLang ? clipLengths(narrationStatus, story, audioLang, voice) : null),
    [story, audioLang, voice, narrationStatus],
  );
  // A memory with a line stays up until its narration is over (plus a breath),
  // and long enough to read its subtitle.
  const lineTimes = useMemo(() => {
    if (!story || (!audioLang && !subtitleLang)) return undefined;
    return new Map(
      story.lines.map((l) => [
        l.mediaId,
        { audioMs: lengths?.lines.get(l.mediaId) ?? 0, text: audioLang ? l[audioLang] : l[subtitleLang ?? 'en'] },
      ]),
    );
  }, [story, audioLang, subtitleLang, lengths]);

  const shots = useMemo(
    () => buildShots(items, { prefs, reducedMotion, photoMotion, lines: lineTimes }),
    // Only the settings that change the edit rebuild it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, prefs.pace, prefs.transition, prefs.motion, reducedMotion, photoMotion, lineTimes],
  );

  // ---- Progress, written straight to the DOM (no per-frame re-render) -------
  const barRef = useRef<HTMLDivElement>(null);
  const timeRef = useRef<HTMLSpanElement>(null);
  const onFrame = useCallback((f: FrameInfo) => {
    const p = f.totalMs > 0 ? Math.min(1, f.positionMs / f.totalMs) : 0;
    if (barRef.current) barRef.current.style.transform = `scaleX(${p})`;
    if (timeRef.current) timeRef.current.textContent = `${clock(f.positionMs)} / ${clock(f.totalMs)}`;
    // Export mode: tell the server how far the recording has got, about once a second.
    const host = exportHost();
    if (host && performance.now() - lastProgress.current > 1000) {
      lastProgress.current = performance.now();
      void host.__exportProgress(f.positionMs, f.totalMs);
    }
  }, []);
  const lastProgress = useRef(0);

  // The film never moves on while the current memory's narration is still
  // being spoken (or fetched, or waiting for a tap), so no line is cut off.
  const audioLangRef = useRef(audioLang);
  audioLangRef.current = audioLang;
  const shotsForGate = useRef(shots);
  shotsForGate.current = shots;
  // A voice waiting for its lead-in also holds the picture.
  const pendingVoice = useRef<{ fire: () => void; remaining: number; startedAt: number; timer: number | null } | null>(null);
  const canAdvance = useCallback((i: number) => {
    if (!audioLangRef.current) return true;
    if (pendingVoice.current) return false;
    if (i === INTRO_INDEX) return !clipBusy('opening');
    const shot = shotsForGate.current[i];
    return !shot || !clipBusy(labelOf({ part: 'line', mediaId: shot.item.id }));
  }, []);

  // The opening card lasts as long as its spoken opening (which starts with the card).
  // The title rises first, then the opening is spoken, then a breath before the first memory.
  const introMs =
    story && (audioLang || subtitleLang) ? Math.max(7500, OPENING_LEAD_MS + (lengths?.opening ?? 0) + 1600) : undefined;

  const film = useFilmEngine({ shots, openingCard, introMs, canAdvance, onFrame });
  /** Export mode: the film is being recorded, so no controls or buttons are shown. */
  const recording = exportLang !== null;
  const { phase, index } = film;
  const current = index >= 0 ? shots[index] : undefined;

  // ---- Voice narration of the story lines (ElevenLabs audio) ---------------------
  // Opening on the title card, one line per memory, closing on the end card.
  // Follows play / pause; moving to another memory stops the previous line;
  // switching the audio language reads the current part again in the new one.
  const spokenKey = useRef<string | null>(null);
  const [narrationError, setNarrationError] = useState<string | null>(null);
  const [, setNarrationTick] = useState(0);
  useEffect(() => onNarrationChange(() => setNarrationTick((t) => t + 1)), []);
  const narrationNow = narrationSnapshot();
  useEffect(() => {
    // A fresh start: nothing has been said yet (also after a remount).
    resetNarration();
    spokenKey.current = null;
    return () => resetNarration();
  }, []);
  // Each line starts after its memory's lead-in (the picture arrives first).
  // The wait pauses with the film and is dropped when the film moves on.
  const cancelVoice = () => {
    const p = pendingVoice.current;
    if (p?.timer) window.clearTimeout(p.timer);
    pendingVoice.current = null;
  };
  const armVoice = () => {
    const p = pendingVoice.current;
    if (!p || p.timer) return;
    p.startedAt = performance.now();
    p.timer = window.setTimeout(() => {
      pendingVoice.current = null;
      p.fire();
    }, p.remaining);
  };
  const speakAfter = (delayMs: number, fire: () => void) => {
    cancelVoice();
    if (delayMs <= 0) return fire();
    stopClip();
    pendingVoice.current = { fire, remaining: delayMs, startedAt: 0, timer: null };
    armVoice();
  };
  useEffect(() => () => cancelVoice(), []);
  useEffect(() => {
    if (!audioLang || !story) {
      cancelVoice();
      stopClip();
      spokenKey.current = null;
      return;
    }
    if (phase === 'paused') {
      const p = pendingVoice.current;
      if (p?.timer) {
        window.clearTimeout(p.timer);
        p.timer = null;
        p.remaining = Math.max(0, p.remaining - (performance.now() - p.startedAt));
      }
      pauseClip();
      return;
    }
    const base = { episodeId: episode.id, lang: audioLang, voice: lengths?.voice ?? null };
    const req = (part: 'opening' | 'closing' | 'line', mediaId?: string) => ({
      ...base,
      part,
      mediaId,
      version: lengths?.version(part, mediaId),
    });
    const report = (message: string) => setNarrationError(message);
    if (phase === 'ended') {
      if (spokenKey.current !== `end|${audioLang}`) {
        spokenKey.current = `end|${audioLang}`;
        speakAfter(900, () => playClip(req('closing'), report));
      }
      return;
    }
    // Playing: the same memory (and language) as before means we were resumed, not moved on.
    const key = `${index === INTRO_INDEX ? 'intro' : current ? current.item.id : 'none'}|${audioLang}|${base.voice ?? ''}`;
    if (spokenKey.current === key) {
      if (pendingVoice.current) armVoice();
      else resumeClip();
      return;
    }
    spokenKey.current = key;
    setNarrationError(null);
    if (index === INTRO_INDEX) speakAfter(OPENING_LEAD_MS, () => playClip(req('opening'), report));
    else if (current && storyLines.has(current.item.id)) {
      const id = current.item.id;
      speakAfter(current.voiceDelayMs, () => playClip(req('line', id), report));
    } else {
      cancelVoice();
      stopClip();
    }
    // Fetch the next part while this one plays, so it starts without a gap.
    const next = shots[index === INTRO_INDEX ? 0 : index + 1];
    if (next && storyLines.has(next.item.id)) prefetchClip(req('line', next.item.id));
    else if (!next) prefetchClip(req('closing'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioLang, story, phase, index, current, storyLines, lengths, episode.id, shots]);
  // Opening and closing text appear only once their voice has really started
  // (never while waiting for Play). If the voice cannot play at all, the text shows anyway.
  const voiceStarted = (label: 'opening' | 'closing') =>
    narrationNow.started.includes(label) || narrationNow.history.some((h) => h.label === label && h.state === 'error');
  const openingSpoken = voiceStarted('opening');
  const closingSpoken = voiceStarted('closing');

  // ---- Soundtrack -----------------------------------------------------------
  const mixer = useMemo(() => new SoundtrackMixer(), []);
  const [library, setLibrary] = useState<SoundtrackLibrary | null>(null);
  const [mixerTick, setMixerTick] = useState(0);
  const [needsGesture, setNeedsGesture] = useState(false);
  const [videoSoundBlocked, setVideoSoundBlocked] = useState(false);
  const loadLibrary = useCallback(() => {
    api.soundtracks().then(setLibrary).catch(() => setLibrary({ tracks: [], limits: { maxFileBytes: 0, extensions: [] } }));
  }, []);
  useEffect(loadLibrary, [loadLibrary]);
  useEffect(() => mixer.onChange(() => setMixerTick((t) => t + 1)), [mixer]);
  useEffect(() => () => mixer.destroy(), [mixer]);

  const playlist = useMemo(() => {
    const tracks = library?.tracks ?? [];
    const byId = new Map(tracks.map((t) => [t.id, t]));
    const picked = prefs.playlist.map((id) => byId.get(id)).filter((t): t is NonNullable<typeof t> => Boolean(t));
    if (picked.length > 0) return picked;
    // No songs chosen: the built-in tracks that fit this episode's mood.
    const builtIn = tracks.filter((t) => t.builtIn);
    const inMood = builtIn.filter((t) => t.category === episode.musicMood);
    return inMood.length > 0 ? inMood : builtIn;
  }, [library, prefs.playlist, episode.musicMood]);
  const musicOn = prefs.musicEnabled && playlist.length > 0;

  useEffect(() => {
    mixer.setTracks(musicOn ? playlist.map((t) => ({ id: t.id, url: t.url, title: t.title })) : []);
  }, [mixer, musicOn, playlist]);
  useEffect(() => mixer.setVolume(prefs.musicVolume), [mixer, prefs.musicVolume]);

  // Browsers allow sound only after a gesture. Arriving from the "Play story"
  // click counts; a reload straight into the player does not, so the opening
  // card then waits for an explicit Play.
  useEffect(() => {
    let cancelled = false;
    void mixer.unlock().then((ok) => {
      if (cancelled || ok) return;
      if (openingCard && library && musicOn) {
        setNeedsGesture(true);
        film.pause();
      }
    });
    return () => {
      cancelled = true;
    };
    // Re-check once the library is known.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [library === null, musicOn]);

  // Mirror film state into the mixer.
  useEffect(() => {
    if (!musicOn) {
      mixer.stop(0.8);
      return;
    }
    if (phase === 'playing') {
      if (mixer.state === 'paused') void mixer.resume();
      // A locked mixer waits for the "Tap for sound" gesture instead.
      else if (mixer.state !== 'playing' && mixer.state !== 'locked') void mixer.start();
    } else if (phase === 'paused') mixer.pause();
    else if (phase === 'ended') mixer.stop(2.5);
  }, [phase, musicOn, mixer, mixerTick]);

  // Duck the music under a video's own sound and under the voice-over. After
  // a line it waits a moment before rising, so the short pauses between lines
  // don't make the music pump up and down.
  const videoHasSound = current?.kind === 'video' && current.item.hasAudio !== false && prefs.videoVolume > 0;
  const voiceSpeaking = narrationNow.state === 'playing';
  const [voiceHold, setVoiceHold] = useState(false);
  useEffect(() => {
    if (voiceSpeaking) {
      setVoiceHold(true);
      return;
    }
    const t = window.setTimeout(() => setVoiceHold(false), VOICE_RELEASE_MS);
    return () => window.clearTimeout(t);
  }, [voiceSpeaking]);
  useEffect(() => {
    const playing = phase === 'playing';
    const video = videoHasSound && playing && !videoSoundBlocked;
    const voice = (voiceSpeaking || voiceHold) && playing;
    mixer.setDuckLevel(Math.min(video ? DUCK_LEVEL : 1, voice ? VOICE_DUCK_LEVEL : 1));
  }, [mixer, videoHasSound, phase, videoSoundBlocked, voiceSpeaking, voiceHold]);

  /** Any explicit gesture may unlock audio. */
  const withGesture = useCallback(
    (fn: () => void) => () => {
      void mixer.unlock();
      fn();
    },
    [mixer],
  );

  // First film ever: the opening card asks how photos should move, and the
  // film waits for the answer (which is also the gesture that allows sound).
  const askingAnimation = prefsLoaded && choice === 'ask' && openingCard && index === INTRO_INDEX && !recording;
  useEffect(() => {
    if (askingAnimation) film.pause();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askingAnimation]);
  const chooseAnimation = useCallback(
    async (value: 'still' | 'motion' | 'depth') => {
      update({ photoAnimation: value });
      await mixer.unlock();
      setNeedsGesture(false);
      resumeClip();
      film.play();
    },
    [film, mixer, update],
  );

  const startFromGate = useCallback(async () => {
    await mixer.unlock();
    setNeedsGesture(false);
    resumeClip();
    film.play();
  }, [film, mixer]);

  // The browser refused to start the narration without a tap: wait for one
  // instead of playing on silently (so the opening is never missed).
  const narrationBlocked = narrationNow.state === 'blocked';
  useEffect(() => {
    if (narrationBlocked && phase === 'playing') {
      film.pause();
      setNeedsGesture(true);
    }
  }, [narrationBlocked, phase, film]);

  const enableVideoSound = useCallback(() => {
    for (const v of document.querySelectorAll<HTMLVideoElement>('video[data-film-video]')) {
      v.muted = prefs.videoVolume === 0;
      void v.play().catch(() => undefined);
    }
    setVideoSoundBlocked(false);
    void mixer.unlock();
    resumeClip();
  }, [mixer, prefs.videoVolume]);

  // ---- Audio and subtitle choices (saved together, so neither jumps) ----------
  const chooseAudio = useCallback(
    (v: 'off' | 'en' | 'ur') => {
      setOverride(null);
      update({ narration: v, subtitles: subtitleLang ?? 'off', ...(subtitleLang ? { storyLines: 'on' } : {}) });
      void mixer.unlock();
    },
    [update, subtitleLang, mixer],
  );
  const chooseSubtitles = useCallback(
    (v: 'off' | 'en' | 'ur') => {
      setOverride(null);
      update({ subtitles: v, narration: audioLang ?? 'off', ...(v !== 'off' ? { storyLines: 'on' } : {}) });
    },
    [update, audioLang],
  );
  /** The language the story plays in now (voice and subtitles), for the overlay switch. */
  const shownLang: 'en' | 'ur' = audioLang ?? subtitleLang ?? primary;
  const chooseLanguage = useCallback(
    (l: 'en' | 'ur') => {
      setOverride(null);
      update({ narration: l, subtitles: l, storyLines: 'on' });
    },
    [update],
  );

  // ---- Export mode: the server's hidden Chrome records this film (export.service.ts) ----
  // Once the narration lengths are known (they set the timing), recording
  // starts and the film plays from the opening card to the closing line.
  const recRef = useRef<{ stop: () => Promise<void> } | null>(null);
  const exportStarted = useRef(false);
  useEffect(() => {
    if (recording) film.pause();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const exportReady = recording && prefsLoaded && library !== null && (!story || narrationStatus !== null);
  useEffect(() => {
    if (!exportReady || exportStarted.current) return;
    exportStarted.current = true;
    const host = exportHost();
    if (!host) return;
    void (async () => {
      try {
        recRef.current = await recordForHost(host, { width: window.innerWidth, height: window.innerHeight });
        await mixer.unlock();
        setNeedsGesture(false);
        resetNarration();
        spokenKey.current = null;
        film.restart();
      } catch (err) {
        void host.__exportFailed((err as Error).message || 'Recording failed');
      }
    })();
  }, [exportReady, mixer, film]);
  // The film has ended: stop once the closing line has been spoken (and a breath after it).
  const closingDone = narrationNow.history.some((h) => h.label === 'closing' && (h.state === 'done' || h.state === 'error'));
  useEffect(() => {
    if (!recording || phase !== 'ended') return;
    const wait = !story || !audioLang || closingDone ? 2500 : 20000;
    const t = window.setTimeout(() => {
      const rec = recRef.current;
      recRef.current = null;
      void rec?.stop();
    }, wait);
    return () => window.clearTimeout(t);
  }, [recording, phase, story, audioLang, closingDone]);

  // ---- Chrome (controls) visibility -------------------------------------------
  const [chromeVisible, setChromeVisible] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const hideTimer = useRef<number | null>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  // Height of the bottom controls without their top fade, so subtitles sit just above them.
  const bottomBarRef = useRef<HTMLDivElement>(null);
  const [controlsHeight, setControlsHeight] = useState(104);
  useEffect(() => {
    const el = bottomBarRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setControlsHeight(Math.max(0, el.offsetHeight - 64)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const showChrome = useCallback(() => {
    setChromeVisible(true);
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => {
      // Never hide while the keyboard is inside the controls.
      if (controlsRef.current?.contains(document.activeElement)) return;
      setChromeVisible(false);
    }, CHROME_HIDE_MS);
  }, []);
  useEffect(() => {
    showChrome();
    return () => {
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
    };
  }, [showChrome]);
  const pinned = phase !== 'playing' || settingsOpen || index === INTRO_INDEX;
  const chromeShown = (chromeVisible || pinned) && index !== INTRO_INDEX && phase !== 'ended' && !recording;

  // ---- Exit, fullscreen, history -----------------------------------------------
  const [exiting, setExiting] = useState(false);
  const exit = useCallback(() => {
    setExiting(true);
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    window.setTimeout(onExit, reducedMotion ? 0 : 320);
  }, [onExit, reducedMotion]);

  const [isFs, setIsFs] = useState(Boolean(document.fullscreenElement));
  const fsSupported = typeof document.documentElement.requestFullscreen === 'function';
  useEffect(() => {
    const on = () => setIsFs(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', on);
    return () => {
      document.removeEventListener('fullscreenchange', on);
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    };
  }, []);
  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    else void document.documentElement.requestFullscreen?.().catch(() => undefined);
  }, []);

  const positionRef = useRef(film.positionSeconds);
  positionRef.current = film.positionSeconds;
  const endedRef = useRef(false);
  endedRef.current = phase === 'ended';
  useEffect(() => {
    const save = () => onSaveProgress(episode.id, positionRef.current(), endedRef.current);
    const t = window.setInterval(save, 5000);
    return () => {
      window.clearInterval(t);
      save();
    };
  }, [episode.id, onSaveProgress]);
  useEffect(() => {
    if (phase === 'ended') onSaveProgress(episode.id, positionRef.current(), true);
  }, [phase, episode.id, onSaveProgress]);

  // Pause when the tab is hidden, so no memory plays unseen and the music
  // never runs over a frozen picture. Checked at mount too: a story opened
  // in a background tab starts hidden and fires no change event.
  useEffect(() => {
    const on = () => {
      if (document.hidden) film.pause();
    };
    on();
    document.addEventListener('visibilitychange', on);
    return () => document.removeEventListener('visibilitychange', on);
  }, [film]);

  // ---- Keyboard --------------------------------------------------------------
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName) || t.isContentEditable)) return;
      if (recording) return; // export mode: nobody is at the keyboard
      void mixer.unlock();
      showChrome();
      switch (e.key) {
        case ' ':
        case 'k':
        case 'K':
          if (t?.tagName === 'BUTTON') return; // let Space press the focused button
          e.preventDefault();
          if (needsGesture) void startFromGate();
          else film.togglePlay();
          break;
        case 'ArrowRight':
          e.preventDefault();
          film.next();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          film.prev();
          break;
        case 'm':
        case 'M':
          update({ musicEnabled: !prefs.musicEnabled });
          break;
        case 'f':
        case 'F':
          if (fsSupported) toggleFullscreen();
          break;
        case 'Escape':
          if (document.fullscreenElement) return; // the browser exits fullscreen
          if (settingsOpen) setSettingsOpen(false);
          else exit();
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [film, mixer, showChrome, needsGesture, startFromGate, update, prefs.musicEnabled, fsSupported, toggleFullscreen, settingsOpen, exit]);

  // ---- Stage gestures: click toggles play (mouse); tap shows controls and
  // swipe navigates (touch). -------------------------------------------------
  const pointer = useRef<{ x: number; y: number; t: number; type: string } | null>(null);
  const onPointerDown = (e: React.PointerEvent) => {
    pointer.current = { x: e.clientX, y: e.clientY, t: Date.now(), type: e.pointerType };
  };
  const onPointerUp = (e: React.PointerEvent) => {
    const p = pointer.current;
    pointer.current = null;
    if (!p) return;
    void mixer.unlock();
    const dx = e.clientX - p.x;
    const dy = e.clientY - p.y;
    if (p.type !== 'mouse' && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) film.next();
      else film.prev();
      return;
    }
    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) return;
    if (p.type === 'mouse') {
      if (settingsOpen) setSettingsOpen(false);
      else film.togglePlay();
      showChrome();
    } else if (chromeVisible && !pinned) {
      setChromeVisible(false);
    } else {
      showChrome();
    }
  };

  // ---- Opening/ending text (never fabricated) ---------------------------------
  const dateLine = useMemo(() => {
    const trusted = items.filter((i) => TRUSTED.has(i.capturedAtSource)).map((i) => i.capturedAt).sort();
    if (trusted.length === 0) return null;
    return formatDateRange(trusted[0], trusted[trusted.length - 1]) || null;
  }, [items]);

  const captionParts = current
    ? [current.trustedDate ? formatFullDate(current.item.capturedAt) : null, current.item.locationName].filter(Boolean)
    : [];

  const playingNow = phase === 'playing';
  // The first memory behind the opening card, so the film flows out of it.
  const firstShot = shots[0];
  const openingBackdropUrl = firstShot
    ? firstShot.kind === 'video'
      ? firstShot.item.posterUrl
      : (firstShot.item.displayUrl ?? firstShot.item.url)
    : null;
  const openingBackdrop = openingBackdropUrl ? { url: openingBackdropUrl, durationMs: introMs ?? INTRO_MS } : null;
  // Dev only, and only when asked for with ?debug in the address.
  const showDebug = import.meta.env.DEV && new URLSearchParams(window.location.search).has('debug');
  const narratingOverVideo =
    current?.kind === 'video' && narrationNow.current === `line:${current.item.id}` && narrationNow.state === 'playing';
  const stageFilter = COLOR_FILTERS[prefs.colorTreatment];

  // Dev-only hook so automated checks can read real mixer and engine state.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    (window as unknown as Record<string, unknown>).__memoryflixFilm = {
      mixer: () => mixer.snapshot(),
      phase: () => phase,
      index: () => index,
      layers: () => film.layers.map((l) => ({ shot: l.shotIndex, enter: l.enter.kind, leave: l.leave?.kind ?? null })),
      shots: () => shots.map((s) => ({ id: s.item.id, kind: s.kind, holdMs: s.holdMs, tx: s.transitionIn, motion: s.motion?.name ?? null })),
      narration: () => ({
        audioLang,
        subtitleLang,
        introMs,
        ...narrationSnapshot(),
        lengths: lengths ? { opening: lengths.opening, closing: lengths.closing, lines: Object.fromEntries(lengths.lines) } : null,
      }),
    };
  });

  return (
    <div
      className={`fixed inset-0 z-50 overflow-hidden bg-[#0b0907] text-[#f3ece2] transition-opacity duration-300 select-none ${
        exiting ? 'opacity-0' : 'opacity-100'
      } ${(chromeShown || !current) && !recording ? '' : 'cursor-none'} ${playingNow ? '' : 'film-paused'} ${reducedMotion ? 'film-static' : ''}`}
      onMouseMove={showChrome}
      role="region"
      aria-label={`Story film: ${episode.title}`}
    >
      {/* ---------- Stage ---------- */}
      <div
        className="absolute inset-0"
        style={{ filter: stageFilter }}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
      >
        {film.layers.map((layer) => {
          const shot = shots[layer.shotIndex];
          if (!shot) return null;
          return (
            <ShotLayer
              key={layer.key}
              layer={layer}
              shot={shot}
              active={!layer.leave}
              playing={playingNow}
              viewportAspect={viewportAspect}
              videoVolume={
                // The video's own sound dips while a line is spoken over it.
                narratingOverVideo && !layer.leave ? prefs.videoVolume * VIDEO_UNDER_VOICE : prefs.videoVolume
              }
              registry={film.registry}
              onExited={film.removeLayer}
              onVideoEnded={film.onVideoEnded}
              onShotError={film.onShotError}
              registerVideo={film.registerVideo}
              onSoundBlocked={() => setVideoSoundBlocked(true)}
            />
          );
        })}
        {prefs.vignette && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{ background: 'radial-gradient(ellipse at center, transparent 55%, rgba(8,6,4,0.55) 100%)' }}
          />
        )}
      </div>

      {/* ---------- Memory caption: date and place, only if recorded ---------- */}
      {prefs.overlays === 'minimal' && current && captionParts.length > 0 && !chromeShown && phase !== 'ended' && (
        <div
          key={current.item.id}
          className="film-caption pointer-events-none absolute bottom-[max(1.75rem,env(safe-area-inset-bottom))] left-6 z-10 max-w-[70vw] text-[#f3ece2]/85 sm:left-10"
          style={{ animationDuration: `${current.holdMs}ms`, textShadow: '0 1px 12px rgba(0,0,0,0.7)' }}
        >
          {captionParts.map((part, i) => (
            <p key={i} className={i === 0 ? 'text-sm' : 'text-xs text-[#f3ece2]/65'}>
              {part}
            </p>
          ))}
        </div>
      )}

      {/* ---------- Story line: AI-written subtitle for this memory ---------- */}
      {subtitleLang && current && storyLines.has(current.item.id) && phase !== 'ended' && (
        <div
          key={`story-${current.item.id}`}
          className={`film-caption pointer-events-none absolute inset-x-0 z-10 mx-auto max-w-[min(90vw,46rem)] px-4 text-center text-[#f3ece2] transition-[bottom] duration-300 ${
            chromeShown ? '' : 'bottom-[max(4rem,env(safe-area-inset-bottom))]'
          }`}
          style={{
            // Narrated: in with the voice, out in the pause after it. Otherwise across the hold.
            ...(audioLang && current.voiceMs > 0
              ? {
                  animationName: 'film-subtitle',
                  animationDelay: `${current.voiceDelayMs}ms`,
                  animationDuration: `${Math.max(1000, current.holdMs - current.voiceDelayMs)}ms`,
                }
              : { animationDuration: `${current.holdMs}ms` }),
            textShadow: '0 1px 3px rgba(0,0,0,0.9), 0 2px 18px rgba(0,0,0,0.75)',
            // Just above the controls, whatever their height (taller on phones).
            ...(chromeShown ? { bottom: `${controlsHeight + 8}px` } : {}),
          }}
          aria-live="polite"
        >
          <StoryText
            line={storyLines.get(current.item.id)!}
            language={subtitleLang}
            className="text-[clamp(1rem,2.3vw,1.5rem)] leading-snug font-medium"
            urClassName="text-[clamp(1.05rem,2.4vw,1.6rem)]"
          />
        </div>
      )}

      {narrationError && !recording && (
        <p className="pointer-events-none absolute top-3 right-3 z-20 max-w-xs rounded bg-black/70 px-3 py-1.5 text-xs text-amber-200" role="status">
          Narration: {narrationError}
        </p>
      )}

      {/* ---------- Opening card ---------- */}
      {(index === INTRO_INDEX || film.introLeaving) && (
        <OpeningCard
          title={episode.title}
          seasonTitle={seasonTitle}
          dateLine={dateLine}
          location={episode.locationName}
          storyLine={
            story && subtitleLang ? (
              <StoryText line={story.opening} language={subtitleLang} className="text-base sm:text-lg" urClassName="text-lg sm:text-xl" />
            ) : null
          }
          storyLineTiming={audioLang ? (openingSpoken ? 'now' : 'wait') : 'delayed'}
          backdrop={openingBackdrop}
          leaving={film.introLeaving}
          waitingForPlay={(needsGesture || phase === 'paused') && !askingAnimation}
          animationChoice={askingAnimation ? { hasDepth, onChoose: (v) => void chooseAnimation(v) } : null}
          onPlay={() => void startFromGate()}
          recording={recording}
          onSkip={withGesture(film.skipIntro)}
          onGone={film.introGone}
        />
      )}

      {/* ---------- Sound prompts ---------- */}
      {(videoSoundBlocked || (musicOn && mixer.state === 'locked')) && phase === 'playing' && !recording && (
        <button
          onClick={enableVideoSound}
          className="focus-ring absolute top-[max(1rem,env(safe-area-inset-top))] left-1/2 z-40 -translate-x-1/2 rounded-full bg-[#17140f]/90 px-4 py-2 text-xs font-semibold ring-1 ring-[#f3ece2]/15 backdrop-blur"
        >
          Tap for sound
        </button>
      )}

      {/* ---------- Controls ---------- */}
      <div
        ref={controlsRef}
        onFocus={showChrome}
        className={`transition-opacity duration-500 ${chromeShown ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
      >
        <div className="absolute inset-x-0 top-0 z-30 flex items-center gap-2 bg-gradient-to-b from-[#0b0907]/85 to-transparent px-3 pt-[max(0.75rem,env(safe-area-inset-top))] pb-10 sm:gap-3 sm:px-6">
          <button onClick={exit} className="focus-ring rounded-full p-2.5 hover:bg-[#f3ece2]/10" aria-label="Back to story">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold sm:text-base">{episode.title}</p>
            <p className="truncate text-xs text-[#f3ece2]/55">
              {current ? `Memory ${index + 1} of ${shots.length}` : ' '}
              {current && captionParts.length > 0 && <span className="text-[#f3ece2]/40">{`  ${captionParts.join(', ')}`}</span>}
            </p>
          </div>
          <button
            onClick={() => {
              setSettingsOpen((o) => !o);
            }}
            className="focus-ring rounded-full p-2.5 hover:bg-[#f3ece2]/10"
            aria-label="Player settings"
            aria-expanded={settingsOpen}
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path d="M4 7h10M18 7h2M4 17h4M12 17h8" strokeLinecap="round" />
              <circle cx="16" cy="7" r="2" />
              <circle cx="10" cy="17" r="2" />
            </svg>
          </button>
          {fsSupported && (
            <button
              onClick={toggleFullscreen}
              className="focus-ring rounded-full p-2.5 hover:bg-[#f3ece2]/10"
              aria-label={isFs ? 'Exit fullscreen' : 'Fullscreen'}
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                {isFs ? (
                  <path d="M9 3v6H3M15 3v6h6M9 21v-6H3M15 21v-6h6" strokeLinecap="round" />
                ) : (
                  <path d="M3 9V3h6M21 9V3h-6M3 15v6h6M21 15v6h-6" strokeLinecap="round" />
                )}
              </svg>
            </button>
          )}
        </div>

        <div
          ref={bottomBarRef}
          className="absolute inset-x-0 bottom-0 z-30 bg-gradient-to-t from-[#0b0907] via-[#0b0907]/80 to-transparent px-3 pt-16 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6"
        >
          {/* Progress: click to jump to that point in the film. */}
          <div
            className="group mb-2 cursor-pointer py-2"
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              const target = ((e.clientX - r.left) / r.width) * shots.reduce((n, s) => n + s.holdMs, 0);
              let acc = 0;
              for (const s of shots) {
                if (acc + s.holdMs > target) return film.jumpTo(s.index);
                acc += s.holdMs;
              }
            }}
            role="presentation"
          >
            <div className="h-[3px] overflow-hidden rounded-full bg-[#f3ece2]/20 transition-[height] group-hover:h-[5px]">
              <div ref={barRef} className="h-full origin-left rounded-full bg-[#f3ece2]" style={{ transform: 'scaleX(0)' }} />
            </div>
          </div>

          <Filmstrip shots={shots} index={index} onJump={film.jumpTo} reducedMotion={reducedMotion} />

          <div className="mt-2 flex items-center gap-1 sm:gap-2">
            <button onClick={film.prev} className="focus-ring rounded-full p-2.5 hover:bg-[#f3ece2]/10" aria-label="Previous memory">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                <path d="M6 6h2v12H6zm3 6l9 6V6z" />
              </svg>
            </button>
            <button
              onClick={withGesture(needsGesture ? () => void startFromGate() : film.togglePlay)}
              className="focus-ring rounded-full bg-[#f3ece2] p-3 text-[#0b0907] transition hover:bg-white"
              aria-label={playingNow ? 'Pause' : 'Play'}
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                {playingNow ? <path d="M6 4h4v16H6zm8 0h4v16h-4z" /> : <path d="M8 5v14l11-7z" />}
              </svg>
            </button>
            <button onClick={film.next} className="focus-ring rounded-full p-2.5 hover:bg-[#f3ece2]/10" aria-label="Next memory">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                <path d="M16 6h2v12h-2zM6 18l9-6-9-6z" />
              </svg>
            </button>
            <span ref={timeRef} className="ml-2 hidden text-xs text-[#f3ece2]/55 tabular-nums sm:inline">
              0:00
            </span>

            <div className="ml-auto flex min-w-0 items-center gap-2">
              {story && (
                // One switch for the story's language: the voice and the subtitles follow it.
                <div className="flex rounded-full bg-[#f3ece2]/10 p-0.5 text-xs" role="group" aria-label="Story language">
                  {(['en', 'ur'] as const).map((l) => {
                    const on = shownLang === l;
                    return (
                      <button
                        key={l}
                        onClick={withGesture(() => chooseLanguage(l))}
                        aria-pressed={on}
                        className={`focus-ring min-h-10 rounded-full px-3.5 font-semibold transition ${
                          on ? 'bg-[#f3ece2] text-[#0b0907]' : 'text-[#f3ece2]/70 hover:text-[#f3ece2]'
                        }`}
                      >
                        {l === 'en' ? 'English' : 'Urdu'}
                      </button>
                    );
                  })}
                </div>
              )}
              <button
                onClick={withGesture(() => update({ musicEnabled: !prefs.musicEnabled }))}
                disabled={playlist.length === 0}
                title={playlist.length === 0 ? 'Add music in settings' : prefs.musicEnabled ? 'Mute music (M)' : 'Play music (M)'}
                className="focus-ring flex min-w-0 items-center gap-2 rounded-full px-2.5 py-2 text-xs text-[#f3ece2]/70 hover:bg-[#f3ece2]/10 disabled:opacity-40"
                aria-label={prefs.musicEnabled ? 'Mute music' : 'Play music'}
                aria-pressed={musicOn}
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                  <path d="M9 18V6l11-2v12" strokeLinejoin="round" />
                  <circle cx="6.5" cy="18" r="2.5" />
                  <circle cx="17.5" cy="16" r="2.5" />
                  {!musicOn && <path d="M3 3l18 18" strokeLinecap="round" />}
                </svg>
                <span className="hidden max-w-[11rem] truncate md:inline">
                  {musicOn ? (mixer.currentTitle ?? playlist[0]?.title) : playlist.length === 0 ? 'No music' : 'Music off'}
                </span>
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={prefs.musicVolume}
                onChange={(e) => update({ musicVolume: Number(e.target.value) })}
                disabled={!musicOn}
                aria-label="Music volume"
                className="hidden w-24 accent-[#f3ece2] disabled:opacity-30 sm:block"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Dev only: what the narration is doing ---------- */}
      {import.meta.env.DEV && showDebug && story && audioLang && (
        <p
          className="pointer-events-none absolute top-[max(4.5rem,env(safe-area-inset-top))] left-3 z-40 max-w-[calc(100vw-1.5rem)] truncate rounded bg-black/70 px-2 py-1 font-mono text-[10px] text-lime-200/90"
          data-testid="narration-debug"
        >
          {`${audioLang.toUpperCase()} · `}
          {narrationNow.history.length === 0
            ? 'nothing requested'
            : narrationNow.history
                .map((h) => {
                  const name = h.label.startsWith('line:')
                    ? `line ${shots.findIndex((sh) => `line:${sh.item.id}` === h.label) + 1}`
                    : h.label;
                  return `${name}: ${h.state === 'requested' ? 'requested' : h.state}`;
                })
                .slice(-4)
                .join(' · ')}
        </p>
      )}

      {/* ---------- Settings ---------- */}
      {settingsOpen && (
        <SettingsPanel
          prefs={prefs}
          update={update}
          audioLang={audioLang}
          subtitleLang={subtitleLang}
          onAudio={chooseAudio}
          onSubtitles={chooseSubtitles}
          library={library}
          onLibraryChange={loadLibrary}
          hasDepth={hasDepth}
          onClose={() => setSettingsOpen(false)}
        />
      )}

      {/* ---------- Ending ---------- */}
      {phase === 'ended' && (
        <EndingCard
          full={prefs.endingCard}
          title={episode.title}
          seasonTitle={seasonTitle}
          dateLine={dateLine}
          location={episode.locationName}
          storyLine={
            story && subtitleLang ? (
              <StoryText line={story.closing} language={subtitleLang} className="text-base italic" urClassName="text-lg" />
            ) : null
          }
          storyLineTiming={audioLang ? (closingSpoken ? 'now' : 'wait') : 'delayed'}
          memoryCount={shots.length}
          recording={recording}
          download={canDownload ? <DownloadVideoButton episodeId={episode.id} lang={story ? shownLang : null} tone="film" /> : null}
          nextTitle={nextEpisode?.title ?? null}
          onReplay={withGesture(() => {
            setNeedsGesture(false);
            resetNarration();
            spokenKey.current = null;
            film.restart();
          })}
          onBack={exit}
          onNext={nextEpisode ? () => onNext(nextEpisode.id) : null}
        />
      )}
    </div>
  );
}
